import type { FC } from 'react';
import Button from '@components/Button';
import InputCheckbox from '@components/InputCheckbox';
import clsx from 'clsx';

interface MultiSelectPanelProps {
  isOpen: boolean;
  selectedCount: number;
  totalCount: number;
  isSubmitting: boolean;
  onToggleSelectAll: () => void;
  onRemove: () => void;
}

export const MultiSelectPanel: FC<MultiSelectPanelProps> = ({
  isOpen,
  selectedCount,
  totalCount,
  isSubmitting,
  onToggleSelectAll,
  onRemove,
}) => {
  const allSelected = totalCount > 0 && selectedCount === totalCount;
  const isIndeterminate = selectedCount > 0 && selectedCount !== totalCount;

  return (
    <div
      data-testid="multi-select-panel"
      className={clsx(
        'shrink-0 overflow-hidden transition-all duration-200 ease-in-out',
        isOpen ? 'max-h-[72px]' : 'max-h-0',
      )}
    >
      <div
        className="border-info-200 flex items-center gap-3 border-t bg-gray-50 px-3 py-2.5"
        role="region"
        aria-label="Bulk block actions"
        inert={!isOpen}
      >
        <InputCheckbox
          isChecked={allSelected}
          isIndeterminate={isIndeterminate}
          onChange={onToggleSelectAll}
          labelText="Select all"
          dataTestId="multi-select-panel:select-all"
          aria-checked={isIndeterminate ? 'mixed' : allSelected}
          className="text-sm whitespace-nowrap"
        />
        <span
          className="bg-primary-100 text-primary-700 inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap"
          data-testid="multi-select-panel:count"
        >
          {selectedCount} selected
        </span>
        <div className="ml-auto">
          <Button
            kind="secondary"
            size="sm"
            iconName="trashcan"
            className="h-auto gap-1.5 px-2 py-1 pr-2.5 text-xs"
            onClick={onRemove}
            loading={isOpen && isSubmitting}
            isDisabled={isSubmitting || selectedCount === 0}
            data-testid="multi-select-panel:remove"
          >
            Remove
          </Button>
        </div>
      </div>
    </div>
  );
};
